/* The widget system — v2's customizable dashboard grid.
   A surface declares its widgets as WidgetDefs; useWidgetGrid() keeps a
   persisted layout (order + hidden, localStorage `mc.widgets.<surface>` —
   per-user D1 storage is a noted later step) and the shell's Customize button
   flips the shared edit mode. In edit mode every widget grows a control bar
   (move up/down — mobile-safe — plus hide, and drag-to-reorder on desktop)
   and an "+ Add widget" card offers the hidden ones back. Clean empty state
   when everything is hidden. Keyboard + AA: all controls are real buttons
   with labels; drag is an enhancement, never the only path. */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Badge, Button, Dialog, IconButton } from '../ds';
import { Kicker } from './ui';

export interface WidgetDef {
  id: string;
  title: string; // human name in the edit bar + picker
  desc: string; // one-liner in the picker
  span: 'full' | 'half';
  node: ReactNode;
}

interface LayoutState {
  order: string[];
  hidden: string[];
}

/* ---- shared edit mode (Topbar button ↔ surfaces) ---- */
interface EditCtx {
  editing: boolean;
  setEditing: (b: boolean) => void;
  /** The surface currently hosting a widget grid ('' = none → hide the button). */
  gridSurface: string;
  registerGrid: (surface: string) => () => void;
}

const WidgetEditCtx = createContext<EditCtx | null>(null);

export function WidgetEditProvider({ children }: { children: ReactNode }) {
  const [editing, setEditing] = useState(false);
  const [gridSurface, setGridSurface] = useState('');
  const registerGrid = useCallback((surface: string) => {
    setGridSurface(surface);
    return () => {
      setGridSurface('');
      setEditing(false); // leaving the surface always exits edit mode
    };
  }, []);
  const value = useMemo(() => ({ editing, setEditing, gridSurface, registerGrid }), [editing, gridSurface, registerGrid]);
  return <WidgetEditCtx.Provider value={value}>{children}</WidgetEditCtx.Provider>;
}

export function useWidgetEdit(): EditCtx {
  const c = useContext(WidgetEditCtx);
  if (!c) throw new Error('useWidgetEdit must be used within <WidgetEditProvider>');
  return c;
}

/* ---- persisted per-surface layout ---- */
const keyFor = (surface: string) => `mc.widgets.${surface}`;

function loadLayout(surface: string, defs: WidgetDef[]): LayoutState {
  const ids = defs.map((d) => d.id);
  const fallback: LayoutState = { order: ids, hidden: [] };
  try {
    const raw = localStorage.getItem(keyFor(surface));
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<LayoutState>;
    // Reconcile with the registry: drop unknown ids, append new widgets at the end.
    const known = (parsed.order ?? []).filter((id) => ids.includes(id));
    const order = [...known, ...ids.filter((id) => !known.includes(id))];
    const hidden = (parsed.hidden ?? []).filter((id) => ids.includes(id));
    return { order, hidden };
  } catch {
    return fallback;
  }
}

export interface WidgetGrid {
  visible: WidgetDef[];
  hiddenDefs: WidgetDef[];
  editing: boolean;
  move: (id: string, dir: -1 | 1) => void;
  moveTo: (id: string, beforeId: string) => void;
  hide: (id: string) => void;
  show: (id: string) => void;
  reset: () => void;
}

export function useWidgetGrid(surface: string, defs: WidgetDef[]): WidgetGrid {
  const { editing, registerGrid } = useWidgetEdit();
  const [layout, setLayout] = useState<LayoutState>(() => loadLayout(surface, defs));

  useEffect(() => registerGrid(surface), [surface, registerGrid]);
  useEffect(() => {
    try {
      localStorage.setItem(keyFor(surface), JSON.stringify(layout));
    } catch {
      /* private mode — layout just doesn't persist */
    }
  }, [surface, layout]);

  const byId = useMemo(() => new Map(defs.map((d) => [d.id, d])), [defs]);
  const visible = layout.order.filter((id) => !layout.hidden.includes(id)).map((id) => byId.get(id)!).filter(Boolean);
  const hiddenDefs = layout.hidden.map((id) => byId.get(id)!).filter(Boolean);

  const move = useCallback((id: string, dir: -1 | 1) => {
    setLayout((l) => {
      const shown = l.order.filter((x) => !l.hidden.includes(x));
      const i = shown.indexOf(id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= shown.length) return l;
      // Swap within the visible sequence, then rebuild the full order.
      const swapped = [...shown];
      [swapped[i], swapped[j]] = [swapped[j], swapped[i]];
      let k = 0;
      return { ...l, order: l.order.map((x) => (l.hidden.includes(x) ? x : swapped[k++])) };
    });
  }, []);

  const moveTo = useCallback((id: string, beforeId: string) => {
    setLayout((l) => {
      if (id === beforeId) return l;
      const order = l.order.filter((x) => x !== id);
      const at = order.indexOf(beforeId);
      if (at < 0) return l;
      order.splice(at, 0, id);
      return { ...l, order };
    });
  }, []);

  const hide = useCallback((id: string) => setLayout((l) => ({ ...l, hidden: [...new Set([...l.hidden, id])] })), []);
  const show = useCallback((id: string) => setLayout((l) => ({ ...l, hidden: l.hidden.filter((x) => x !== id) })), []);
  const reset = useCallback(() => setLayout({ order: defs.map((d) => d.id), hidden: [] }), [defs]);

  return { visible, hiddenDefs, editing, move, moveTo, hide, show, reset };
}

/* ---- the grid + frames + picker ---- */
const ICON = {
  up: <path d="m18 15-6-6-6 6" />,
  down: <path d="m6 9 6 6 6-6" />,
  hide: <path d="M18 6 6 18M6 6l12 12" />,
  grip: <path d="M9 6h.01M9 12h.01M9 18h.01M15 6h.01M15 12h.01M15 18h.01" />,
  plus: <path d="M12 5v14M5 12h14" />,
};

const editIcon = (d: ReactNode, size = 15) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    {d}
  </svg>
);

export function WidgetGridView({ grid, label }: { grid: WidgetGrid; label: string }) {
  const dragId = useRef<string | null>(null);
  const [picker, setPicker] = useState(false);
  const { visible, hiddenDefs, editing, move, moveTo, hide, show, reset } = grid;

  return (
    <div className="mc-widgetgrid" role="list" aria-label={`${label} widgets`}>
      {visible.map((d, i) => (
        <div
          key={d.id}
          role="listitem"
          className={d.span === 'full' ? 'mc-span-full' : undefined}
          draggable={editing}
          onDragStart={(e) => {
            dragId.current = d.id;
            e.dataTransfer.effectAllowed = 'move';
          }}
          onDragOver={(e) => {
            if (editing && dragId.current && dragId.current !== d.id) e.preventDefault();
          }}
          onDrop={(e) => {
            e.preventDefault();
            if (dragId.current) moveTo(dragId.current, d.id);
            dragId.current = null;
          }}
          style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}
        >
          {editing && (
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px',
                border: '1px dashed var(--primary-line)', borderRadius: 'var(--radius-md)',
                background: 'var(--primary-soft)', cursor: 'grab',
              }}
            >
              <span aria-hidden="true" style={{ display: 'flex', color: 'var(--muted)' }}>{editIcon(ICON.grip)}</span>
              <Kicker style={{ flex: 1, color: 'var(--ink)' }}>{d.title}</Kicker>
              <span className="mc-editbtns" style={{ display: 'flex', gap: 2 }}>
                <IconButton variant="ghost" size="sm" label={`Move ${d.title} up`} onClick={() => move(d.id, -1)} disabled={i === 0}>
                  {editIcon(ICON.up)}
                </IconButton>
                <IconButton variant="ghost" size="sm" label={`Move ${d.title} down`} onClick={() => move(d.id, 1)} disabled={i === visible.length - 1}>
                  {editIcon(ICON.down)}
                </IconButton>
                <IconButton variant="ghost" size="sm" label={`Hide ${d.title}`} onClick={() => hide(d.id)}>
                  {editIcon(ICON.hide)}
                </IconButton>
              </span>
            </div>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>{d.node}</div>
        </div>
      ))}

      {/* Empty state — everything hidden. */}
      {visible.length === 0 && (
        <div className="mc-span-full" style={{ border: '1.5px dashed var(--border)', borderRadius: 'var(--radius-lg)', padding: '38px 20px', textAlign: 'center', color: 'var(--muted)' }}>
          <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 15, color: 'var(--ink)', marginBottom: 6 }}>No widgets on this surface</div>
          <div style={{ fontSize: 13, marginBottom: 14 }}>Everything is hidden. Add widgets back, or reset the layout.</div>
          <span style={{ display: 'inline-flex', gap: 8 }}>
            <Button variant="secondary" size="sm" onClick={() => setPicker(true)}>Add widget</Button>
            <Button variant="ghost" size="sm" onClick={reset}>Reset layout</Button>
          </span>
        </div>
      )}

      {/* + Add widget — the real one (replaces the old static slot). */}
      {editing && visible.length > 0 && (
        <button
          onClick={() => setPicker(true)}
          aria-label="Add a widget"
          style={{
            border: '1.5px dashed var(--primary-line)', borderRadius: 'var(--radius-lg)', minHeight: 110,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
            color: 'var(--primary)', cursor: 'pointer', background: 'none',
          }}
        >
          {editIcon(ICON.plus, 20)}
          <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 13.5 }}>Add widget</span>
          <Kicker>// {hiddenDefs.length} available</Kicker>
        </button>
      )}

      <Dialog open={picker} title="Add a widget" onClose={() => setPicker(false)}>
        {hiddenDefs.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--muted)', fontSize: 13.5 }}>Every widget is already on the surface.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {hiddenDefs.map((d) => (
              <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 13.5, color: 'var(--ink)' }}>{d.title}</span>
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--muted)' }}>{d.desc}</span>
                </span>
                <Button size="sm" variant="secondary" onClick={() => { show(d.id); }}>
                  Add
                </Button>
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Badge tone="neutral">layout saved on this device</Badge>
          <Button variant="ghost" size="sm" onClick={reset}>Reset layout</Button>
        </div>
      </Dialog>
    </div>
  );
}
